import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { Feature, FeatureId, FeatureLocation, FeatureRegistry } from '../types/platform';
import { usePlatform } from './PlatformContext';

const FeatureContext = createContext<FeatureRegistry | null>(null);

interface FeatureProviderProps {
  children: ReactNode;
  initialFeatures?: Feature[];
}

export function FeatureProvider({ children, initialFeatures = [] }: FeatureProviderProps) {
  const platform = usePlatform();
  const [features, setFeatures] = useState<Map<FeatureId, Feature>>(
    () => new Map(initialFeatures
      .filter(f => f.platform === 'all' || f.platform === platform.name)
      .filter(f => !f.isAvailable || f.isAvailable())
      .map(f => [f.id, f])
    )
  );

  const register = useCallback(<TProps,>(feature: Feature<TProps>) => {
    if (feature.platform !== 'all' && feature.platform !== platform.name) {
      return;
    }

    if (feature.isAvailable && !feature.isAvailable()) {
      return;
    }

    setFeatures(prev => new Map(prev).set(feature.id, feature as Feature));
    feature.initialize?.();
  }, [platform.name]);

  const unregister = useCallback((id: FeatureId) => {
    setFeatures(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const get = useCallback(<TProps,>(id: FeatureId): Feature<TProps> | undefined => {
    return features.get(id) as Feature<TProps> | undefined;
  }, [features]);

  const getAll = useCallback((): Feature[] => {
    return Array.from(features.values());
  }, [features]);

  const getByLocation = useCallback((location: FeatureLocation): Feature[] => {
    return Array.from(features.values()).filter(f => f.location === location);
  }, [features]);

  const getByPlatform = useCallback((targetPlatform: 'web' | 'electron'): Feature[] => {
    return Array.from(features.values()).filter(
      f => f.platform === 'all' || f.platform === targetPlatform
    );
  }, [features]);

  const registry: FeatureRegistry = {
    register,
    unregister,
    get,
    getAll,
    getByLocation,
    getByPlatform,
  };

  return (
    <FeatureContext.Provider value={registry}>
      {children}
    </FeatureContext.Provider>
  );
}

export function useFeatures(): FeatureRegistry {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatures must be used within FeatureProvider');
  }
  return context;
}

export function useFeature<TProps = unknown>(id: FeatureId): Feature<TProps> | undefined {
  const registry = useFeatures();
  return registry.get<TProps>(id);
}
